/**
 * Advanced settings (ADR-0027) — the operator-tunable security/behaviour policy.
 *
 * Mirrors `backend/src/hub/routes/settings.py`. Each field has a permissive
 * code default; the operator can override it at runtime (DB-backed, no env vars).
 * The stored blob is sparse (only divergences from the default), so `overridden`
 * lists the keys the operator deliberately changed. Gated `settings:read` /
 * `settings:write`.
 */
import { api } from "./api";

export interface AdvancedSettingsValues {
  http_block_private: boolean;
  http_allow_hosts: string;
  db_block_private: boolean;
  db_allow_hosts: string;
  db_connect_timeout: number;
  webhook_strict: boolean;
  webhook_hmac_window: number;
  health_probe_enabled: boolean;
  health_probe_interval_seconds: number;
  /** The instance's PUBLIC base URL (e.g. `https://trayos.dev`) — used to build the
   *  OAuth callback `redirect_uri` for interactive MCP auth. Empty ⇒ request origin. */
  public_base_url: string;
  /** Per-entity upload size limits in MB, keyed by upload-entity key (chat, kb, …). */
  upload_limits_mb: Record<string, number>;
  /** The tenant's TOTAL monthly AI spend cap in USD; 0 = uncapped. A total, not a
   *  per-config cap: the bill arrives per API key and configs can share one. */
  ai_monthly_budget_usd: number;
}

/** A registered upload surface — drives one MB field in the Settings form. */
export interface UploadEntity {
  key: string;
  label: string;
  default_mb: number;
}

export interface AdvancedSettings {
  /** The values in force (permissive default or operator override per field). */
  effective: AdvancedSettingsValues;
  /** The permissive defaults — the "reset" target + per-field reference. */
  defaults: AdvancedSettingsValues;
  /** Keys the operator overrode (diverged from the default). */
  overridden: string[];
  /** The upload-entity registry (present + future surfaces) to render MB fields for. */
  upload_entities: UploadEntity[];
}

export const settingsApi = {
  getAdvanced: () => api.get<AdvancedSettings>("/settings/advanced"),
  putAdvanced: (body: AdvancedSettingsValues) =>
    api.put<AdvancedSettings>("/settings/advanced", body),
};

/**
 * Partner / support ACCESS (ADR-0081 S3) — the two LOCAL gates.
 *
 * Attribution ("this customer is ACME's") lives on Control and is not editable here.
 * ENTERING is decided by these two values, on this instance, by this customer — which is
 * why the panel is in their settings and not something we flip for them.
 */
export interface PartnerAccess {
  enabled: boolean;
  code: string;
  /** True when the code came from the installer rather than an operator's edit — the UI
   *  says so instead of implying someone here chose it. */
  code_from_install: boolean;
}

/** One VISIT. `active` is derived server-side (an idle-but-unclosed row is not live). */
export interface PartnerSession {
  id: string;
  subject: "partner" | "tray_support";
  display_name: string;
  partner_code: string | null;
  issued_by: string;
  started_at: string;
  last_seen_at: string;
  ended_at: string | null;
  end_reason: string | null;
  active: boolean;
}

export const partnerAccessApi = {
  get: () => api.get<PartnerAccess>("/settings/partner-access"),
  /** `code: ""` clears the stored code; omit it to leave it alone. */
  put: (body: { enabled?: boolean; code?: string }) =>
    api.put<PartnerAccess>("/settings/partner-access", body),
  sessions: () => api.get<PartnerSession[]>("/settings/partner-access/sessions"),
};
